import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import Content from '@/models/content';
import { useContents } from '@/services/contents';
import ContentModal from './components/content-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';
import { useCategories } from '@/services/categories';

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    if (order === 'desc') {
        return (a: any, b: any) => {
            if (a[orderBy] < b[orderBy]) {
                return -1;
            }
            if (a[orderBy] > b[orderBy]) {
                return 1;
            }
            return 0;
        };
    }
    return (a: any, b: any) => {
        if (a[orderBy] > b[orderBy]) {
            return -1;
        }
        if (a[orderBy] < b[orderBy]) {
            return 1;
        }
        return 0;
    };
};

const Contents = () => {
    const { data: categories } = useCategories();
    const { data: contents, isLoading } = useContents();
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filteredContents, setFilteredContents] = useState<Content[]>(contents?.data || []);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedContentId, setSelectedContentId] = useState<string>();

    useEffect(() => {
        if (contents?.data) {
            setFilteredContents(contents.data);
        }
    }, [contents]);

    const toggle = () => {
        setIsOpen((prevState) => !prevState);
    };

    const handleRequestAdd = () => {
        setSelectedContentId(undefined);
        toggle();
    };

    const handleRequestSort = (_: React.MouseEvent<unknown>, property: string) => {
        const orderByTemp = property;
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }

        setOrder(orderTemp);
        setOrderBy(orderByTemp);
    };

    const handleClick = (id: string) => {
        setSelectedContentId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => {
        setPage(item);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Contents</h3>
                    <h3 className="page-subhead subhead">These are the Audio/Video contents</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Title"
                                            type="text"
                                            onChange={(e) =>
                                                setFilteredContents(
                                                    contents?.data?.filter((c) => c.title.toLowerCase().includes(e.target.value.toLowerCase())) || [],
                                                )
                                            }
                                        />
                                        <MatTableToolbar onRequestAdd={handleRequestAdd} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filteredContents
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((d) => {
                                                        return (
                                                            <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                                <TableCell component="th" scope="row" padding="none">
                                                                    {d.id}
                                                                </TableCell>
                                                                <TableCell>{d.title}</TableCell>
                                                                <TableCell>{categories?.data?.find((c) => c.id === d.categoryId)?.name}</TableCell>
                                                                <TableCell>
                                                                    {d.personas?.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' - ')}
                                                                </TableCell>
                                                                <TableCell>{d.mediaType}</TableCell>
                                                                <TableCell>{d.subscriptionPlan}</TableCell>
                                                                <TableCell>{formatDate(d.created_at)}</TableCell>
                                                                <TableCell>{formatDate(d.updated_at)}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                {filteredContents.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={7} className="text-center">
                                                            No Data to display
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filteredContents.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{
                                            inputProps: { 'aria-label': 'rows per page' },
                                            native: true,
                                        }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <ContentModal isOpen={isOpen} toggle={toggle} selectedContentId={selectedContentId} categories={categories?.data || []} />
        </Container>
    );
};

export default Contents;
